import { escapeRegExp } from 'lodash-es';
import { initialUpperCase } from '.';
import React from 'react';
import SvgIcon from '@home/components/SvgIcon';
import DOMPurify from 'dompurify';
import MarkdownIt from 'markdown-it';
import markdownItAnchor from 'markdown-it-anchor';
import markdownItTocDoneRight, { TocAst } from 'markdown-it-toc-done-right';
import uslug from 'uslug';
import { isNilOrEmpty } from './value';
import DBO_FWC from '@op/hooks/useFrontWebConfig';
import { notification } from 'tea-component';

export type KeywordRenderType = {
  text: string;
  keyword?: string | Array<string>;
  sentimentInfo?: string;
  titleCase?: boolean;
};

export const SpecialSymbol: { [key: string]: string } = {
  LEFT: '┠┨',
  RIGHT: '┠▓┨',
  LEFT_1: '┠◢┨',
  RIGHT_1: '┠▓◢┨',
  LEFT_3: '┠◣┨',
  RIGHT_3: '┠▓◣┨',
  LEFT_5: '┠█┨',
  RIGHT_5: '┠▓█┨',
};

function sentimentRender(text: string, sentimentInfo: string) {
  try {
    let {
      sentiment,
      index,
    }: { sentiment: Array<1 | 3 | 5>; index: Array<number> } = JSON.parse(
      sentimentInfo || '{}',
    );
    sentiment = sentiment.filter((item) => [1, 3, 5].includes(item));
    if (sentiment.length === 0) return { text, isDealwith: false };
    let newText = '';

    sentiment.forEach((item, i) => {
      const start = index[i - 1] || 0;
      const end = index[i] || text.length;

      if (end && end > 0) {
        const leftSymbol = SpecialSymbol[`LEFT_${item}`] || '';
        const rightSymbol = SpecialSymbol[`RIGHT_${item}`] || '';
        newText += `${leftSymbol}${text.substring(start, end)}${rightSymbol}`;
      }
    });
    return { text: newText || text, isDealwith: true };
  } catch (error) {
    return { text, isDealwith: false };
  }
}

function replaceKeywords(
  text: string,
  keyword: string,
  replacement: string | ((value: string) => string),
) {
  const regex = new RegExp(escapeRegExp(keyword), 'gi');
  return text.replace(regex, (match: string) => {
    const text =
      typeof replacement === 'string' ? replacement : replacement(match);
    return text;
  });
}

function markKeyword(text: string, keyword: string) {
  return replaceKeywords(
    text,
    keyword,
    (info: string) => `${SpecialSymbol.LEFT}${info}${SpecialSymbol.RIGHT}`,
  );
}

function markKeywordInfo(
  text: string,
  keyword: string | Array<string>,
  isFilter = false,
) {
  if (!keyword) return text;
  const isArr = Array.isArray(keyword);
  if (!isArr && typeof keyword !== 'string') return text;
  if (isArr && keyword.length === 0) return text;

  let renderKeyword: Array<string> =
    typeof keyword === 'string' ? [keyword] : keyword;

  if (renderKeyword.length > 1 || isFilter) {
    // 过滤
    renderKeyword = renderKeyword.filter(
      (item) =>
        ![
          '┠',
          '┨',
          '▓',
          '┠┨',
          '┠▓┨',
          '┠▓',
          '▓┨',
          '┠◢┨',
          '┠▓◢┨',
          '▓◢┨',
          '◢┨',
          '┠◢',
          '◢',
          '◣',
          '█',
          '┠◣',
          '┠◣┨',
          '┠▓◣┨',
          '▓◣┨',
          '◣┨',
          '┠█',
          '┠█┨',
          '┠▓█┨',
          '▓█┨',
          '█┨',
        ].includes(item),
    );
  }

  // 排序,保证大词先进行标记，再进行小词标记
  renderKeyword.sort((a, b) => b.localeCompare(a, 'zh-Hans-CN'));

  renderKeyword.forEach((item) => {
    text = markKeyword(text, item);
  });

  return text;
}

function replaceAllTag(text: string) {
  const replaceList = [
    { tag: SpecialSymbol.RIGHT_1, replaceTag: '</Fragment>' },
    { tag: SpecialSymbol.RIGHT_3, replaceTag: '</Fragment>' },
    { tag: SpecialSymbol.RIGHT_5, replaceTag: '</Fragment>' },
    {
      tag: SpecialSymbol.LEFT_1,
      replaceTag: "<Fragment class='dbo-text-highlight-negative'>",
    },
    {
      tag: SpecialSymbol.LEFT_3,
      replaceTag: "<Fragment class='dbo-text-highlight-neutral'>",
    },
    {
      tag: SpecialSymbol.LEFT_5,
      replaceTag: "<Fragment class='dbo-text-highlight-positive'>",
    },
    {
      tag: SpecialSymbol.LEFT,
      replaceTag: "<Fragment class='dbo-text-highlight'>",
    },
    { tag: SpecialSymbol.RIGHT, replaceTag: '</Fragment>' },
  ];

  replaceList.forEach(({ tag, replaceTag }) => {
    text = replaceKeywords(text, tag, replaceTag);
  });

  return text;
}

// 安全转义函数
export const escapeAllTags = (text: string) => {
  if (typeof text === "string") {
    return text?.replace(/</g, '&lt;')?.replace(/>/g, '&gt;');
  }
  return text;
};

export function keywordRenderFunc({
  text,
  keyword,
  sentimentInfo,
  titleCase = false,
}: KeywordRenderType): JSX.Element | string {
  if (!text || typeof text !== 'string') return text;

  if (titleCase) text = initialUpperCase(text);

  let newText = text;
  let isFilter = false;
  if (sentimentInfo) {
    const info = sentimentRender(text, sentimentInfo);
    newText = info.text;
    isFilter = info.isDealwith;
  }
  if (keyword) newText = markKeywordInfo(newText, keyword, isFilter);

  return <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(replaceAllTag(newText)) }} />;
}

/**
 * sub_str_index :0:28_30:58
 * 标记句子中的两个位置：0-28,30-58
 * 先将这两个位置解析出来，然后通过substring对文本进行切割为两个子字符串
 * 最后将这两个子字符作为keyword参数传入到markKeywordInfo中进行标记
 * @param text 需要进行标记的文本
 * @param subStrIndex 标记位置
 * @returns
 */
export function subForKeywordRender({
  text,
  subStrIndex,
}: {
  text: string;
  subStrIndex: string;
}) {
  if (typeof subStrIndex !== 'string' || !subStrIndex) {
    return text;
  }
  try {
    const indexList = subStrIndex.split('_').map((item) => {
      const [begin, end] = item.split(':');
      return {
        begin: parseInt(begin),
        end: parseInt(end),
      };
    });

    const keyword = indexList.map((item) =>
      text.substring(item.begin, item.end),
    );
    return keywordRenderFunc({
      text,
      keyword,
    });
  } catch (error) {
    return text;
  }
}

interface Comment {
  comment_parent_id: string;
  media_type: string;
  is_danmaku?: boolean;
}

const DEFAULT_MEDIA_TYPE = 'text';
const COMMENT_PARENT_ID_DEFAULT = '-1';
const INDEX_FOR_CHART_LIVE = 2;

const mediaTypeReviewsType: { [key: string]: Array<string> } = {
  text: ['Text Posts', 'Comments on Text Posts'],
  video: ['Video Titles', 'Comments On Videos'],
  live: ['Stream Titles', 'Comments on Streams', 'Live Chats on Streams'],
};

export function getFeedsReviewsType(comment: Comment) {
  // 使用解构赋值，并且直接处理可能的未定义属性
  const {
    comment_parent_id = COMMENT_PARENT_ID_DEFAULT,
    media_type,
    is_danmaku,
  } = comment;

  // 确保realMediaType总是有值，且是mediaTypeReviewsType中的一个键
  const realMediaType: string = ['video', 'live'].includes(media_type)
    ? media_type
    : DEFAULT_MEDIA_TYPE;

  const reviewTypes = mediaTypeReviewsType[realMediaType];

  // 检查reviewTypes是否存在，如果不存在抛出错误
  if (!reviewTypes) {
    console.warn(`No review types found for media type: ${realMediaType}`);
    return '';
  }

  // 使用常量代替魔法值，并确保index的类型安全
  let index: number = comment_parent_id === COMMENT_PARENT_ID_DEFAULT ? 0 : 1;
  if (is_danmaku && media_type === 'live') {
    index = INDEX_FOR_CHART_LIVE;
  }

  // 检查index是否越界，如果越界同样抛出错误
  if (index >= reviewTypes.length) {
    console.warn(`Index out of bounds for media type: ${realMediaType}`);
    return '';
  }

  return reviewTypes[index];
}
export function calculateSteamReviewRate(value = 0) {
  // 输入验证：确保value是一个在0到1之间的数字
  if (typeof value !== 'number' || value < 0 || value > 1 || isNaN(value)) {
    value = 0;
  }

  // 使用对象映射来改善可读性和维护性
  const leReviewRatingMap: any = {
    '0.2': 'Overwhelmingly Negative',
    '0.3': 'Very Negative',
    '0.4': 'Mostly Negative',
  };

  const ltReviewRatingMap: any = {
    '0.7': 'Mixed',
    '0.8': 'Mostly Positive',
    '0.95': 'Very Positive',
  };

  // 遍历小于等于映射对象，找到第一个满足条件的评价等级并返回
  for (const key in leReviewRatingMap) {
    if (value <= parseFloat(key)) {
      return leReviewRatingMap[key];
    }
  }

  // 遍历小于映射对象，找到第一个满足条件的评价等级并返回
  for (const key in ltReviewRatingMap) {
    if (value < parseFloat(key)) {
      return ltReviewRatingMap[key];
    }
  }

  // 如果没有找到满足条件的评价等级，则返回默认值
  return 'Overwhelmingly Positive';
}

export function calculateBrandHealthRate(data?: number) {
  if (isNilOrEmpty(data)) return { text: '-' };
  let value = Number(data);
  if (isNaN(value)) value = 0;
  if (value > 50) return { text: 'Very Healthy', className: '' };
  if (value >= 25) return { text: 'Healthy', className: '' };
  if (value >= -25) return { text: 'Mixed-1', className: '__mixed' };
  if (value >= -70) return { text: 'Unhealthy', className: '--unhealthy' };
  return { text: 'Very Unhealthy', className: '--unhealthy' };
}

export const getStarIcon = (star: number) => {
  const elements = [];
  for (let index = 0; index < star; index++) {
    elements.push(
      <SvgIcon key={index} type="star-action" className="text-star" />,
    );
  }

  return elements;
};

/**
 * 自定义 MarkdownIt block rule：处理含特定 class 的 HTML 块
 *
 * 问题：MarkdownIt 将 <ul>/<div>/<li> 等标签归为 Type 6 HTML Block，
 * 遇到空行即终止。当这些标签内部存在空行时（如评论段落分隔），
 * MarkdownIt 误判 HTML Block 结束，导致 HTML 结构断裂。
 *
 * 方案：自定义 block rule 注册在默认 html_block 之前，
 * 对匹配特定 class 的 HTML 块按 Type 1 方式处理（用结束标签终止），
 * 不匹配的交给默认 html_block 处理。
 *
 * 优势：
 * 1. 在 MarkdownIt 解析层面解决问题，不需要预处理/后处理
 * 2. 不修改原始内容，不引入占位符
 * 3. 对不匹配的 HTML 标签行为零影响
 */
function dboHtmlBlockRule(state: any, startLine: number, endLine: number, silent: boolean): boolean {
  const pos = state.bMarks[startLine] + state.tShift[startLine];
  const max = state.eMarks[startLine];

  // 快速排除：行长度不够或不是 <ul 开头
  if (pos + 3 > max) return false;
  if (state.src.charCodeAt(pos) !== 0x3C /* < */ ||
    state.src.charCodeAt(pos + 1) !== 0x75 /* u */ ||
    state.src.charCodeAt(pos + 2) !== 0x6C /* l */) {
    return false;
  }

  // 检查是否为匹配特定 class 的 <ul> 标签
  const lineText = state.src.slice(pos, max);
  if (!/<ul\s+[^>]*class="[^"]*dbo-md-comments-ul-list[^"]*"/i.test(lineText)) {
    return false;
  }

  if (silent) return true;

  // 按 Type 1 方式处理：通过标签计数器找到匹配的 </ul>，不受空行影响
  let depth = 1;
  let nextLine = startLine + 1;

  while (nextLine < endLine && depth > 0) {
    const linePos = state.bMarks[nextLine] + state.tShift[nextLine];
    const lineMax = state.eMarks[nextLine];
    const line = state.src.slice(linePos, lineMax);

    // 计算 <ul> 和 </ul> 的数量（考虑嵌套）
    const openMatches = line.match(/<ul[\s>]/gi);
    const closeMatches = line.match(/<\/ul\s*>/gi);

    if (openMatches) depth += openMatches.length;
    if (closeMatches) depth -= closeMatches.length;

    nextLine += 1;
  }

  // 生成 html_block token
  const token = state.push('html_block', '', 0);
  token.content = state.getLines(startLine, nextLine, state.blkIndent, true);
  token.map = [startLine, nextLine];

  state.line = nextLine;
  return true;
}

export function mdContentToHtml(text: string) {
  const ALLOWED_TAGS = [
    'div',
    'span',
    'ul',
    'ol',
    'li',
    'a',
    'font',
    'img',
    'p',
    'br',
    'hr',
    'strong',
    'b',
    'em',
    'i',
    'blockquote',
    'code',
    'pre',
    'table',
    'thead',
    'tbody',
    'tr',
    'td',
    'th',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
  ];

  const tagRegex = new RegExp(
    `<\\/?(?:${ALLOWED_TAGS.join('|')})(?:\\s[^<>]*?)?\\/?>`,
    'gi'
  );

  const entityRegex = /&(?:[a-zA-Z]+|#\d+|#x[a-fA-F0-9]+);/g;

  function escapeBrokenHtml(text: string) {
    const tags: string[] = [];
    const entities: string[] = [];

    let result = text
      .replace(entityRegex, m => {
        entities.push(m);
        return `__ENT_${entities.length - 1}__`;
      })
      .replace(tagRegex, m => {
        tags.push(m);
        return `__TAG_${tags.length - 1}__`;
      });

    result = result
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    result = result
      .replace(/__TAG_(\d+)__/g, (_, i) => tags[+i])
      .replace(/__ENT_(\d+)__/g, (_, i) => entities[+i]);

    return result;
  }

  const safeText = escapeBrokenHtml(text);
  const md = new MarkdownIt({ html: true });

  // 注册自定义 block rule，在默认 html_block 之前执行
  md.block.ruler.before('html_block', 'dbo_html_block', dboHtmlBlockRule);

  const defaultRender = md.renderer.rules.image;

  if (defaultRender) {
    md.renderer.rules.image = function (tokens, idx, options, env, self) {
      const token = tokens[idx];
      const srcIndex = token.attrIndex('src');
      if (srcIndex >= 0 && token.attrs) {
        const src = token.attrs[srcIndex][1];
        const urlPattern =
          /^(https?:\/\/)?[\da-z.-]+\.[a-z.]{2,6}(\/[\w .-]*)*\/?$/;
        if (!urlPattern.test(src)) {
          console.log('非正常的链接', src);
          return '';
        }
        // console.log('正常的链接', src);
      }
      return defaultRender(tokens, idx, options, env, self);
    };
  }
  const headings: any = [];
  let navigator: any = [];
  const renderedHtml = md
    .use(markdownItAnchor, { slugify: uslug })
    .use(markdownItTocDoneRight, {
      callback: (_, ast) => {
        function addChildrenToHeading(curAst: TocAst) {
          const { l, n, c } = curAst;
          if (l > 0 && l < 4) {
            headings.push({ level: l, text: n, slug: uslug(n) });
          }

          if (Array.isArray(c) && c.length > 0) {
            c.forEach((item) => {
              addChildrenToHeading(item);
            });
          }
        }
        addChildrenToHeading(ast);
        navigator = headings[0]?.slug;
      },
      slugify: uslug,
    })
    .render(safeText);

  const html = DOMPurify.sanitize(renderedHtml, {
    ADD_ATTR: ['target'],
    FORBID_TAGS: ['s'],
  });

  return {
    html,
    headings,
    navigator,
  };
}

/**
 * 将html元素处理li后转换为纯文本
 * @param html
 * @returns
 */
export function htmlToText(html: HTMLDivElement) {
  const clonedHTML = html.cloneNode(true) as HTMLDivElement;
  function processLists(node: ParentNode) {
    const lists = node.querySelectorAll('ol');
    lists.forEach((ol) => {
      // 读取 ol 自身的 start 属性，用于非嵌套的独立列表
      const olStart = parseInt(ol.getAttribute('start') || '', 10) || 1;
      Array.from(ol.children)
        .filter((child) => child.tagName === 'LI')
        .forEach((li, index) => {
          const parentElement = ol.closest('li');
          let start = '';
          if (parentElement) {
            // 嵌套列表：从父级 li 读取累积的 start 标记
            start = parentElement.getAttribute('start') || '';
          }
          // 计算当前项的实际序号：嵌套列表用 index+1，独立列表用 olStart+index
          const itemNumber = parentElement ? index + 1 : olStart + index;
          const indexText = `${itemNumber}.`;
          let begin = '';
          const listLength = start.split('.').length - 1;
          for (let i = 0; i < listLength; i++) {
            begin += '\0\0\0';
          }

          const markerText = `${begin}${start ? start : ''}${indexText} `;
          const marker = document.createTextNode(markerText);

          li?.insertBefore?.(marker, li.firstChild);
          li?.setAttribute?.('start', start + indexText);
        });
    });
  }

  processLists(clonedHTML);

  return clonedHTML.innerText || clonedHTML.textContent || '';
}

/**
 * 将markdown文本转换为纯文本
 * @param text
 * @returns
 */
export function markdownToText(text: string): string {
  const { html } = mdContentToHtml(text);
  const divElement = document.createElement('div');
  divElement.innerHTML = html;

  return htmlToText(divElement);
}

export function markdownToHtmlFile(text: string): string {
  const { html } = mdContentToHtml(text);
  return `
  <!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Document</title>
</head>
<body>
    ${html}
</body>
</html>
  `;
}

/**
 * 将md转为text后进行复制
 * @param text
 * @returns
 */
export async function copyMarkdownText(text: string): Promise<void> {
  const innerText = markdownToText(text);
  return copyTextToClipBoard(innerText, ({ id: data }) => data);
}

function copySuccessTips(formatMessage: any) {
  notification.success({
    description: formatMessage({ id: 'Copied successfully' }),
  });
}

function copyErrorTips(formatMessage: any) {
  notification.error({
    description: formatMessage({ id: 'Copied failed' }),
  });
}

/**
 * 复制文案到剪切板
 * @param text
 * @returns
 */
export function copyTextToClipBoard(
  text: any,
  formatMessage: (item: { id: string }) => string,
) {
  const textarea = document.createElement('textarea'); // 创建input对象
  const currentFocus = document.activeElement; // 当前获得焦点的元素
  let flag = false;
  document.body.appendChild(textarea); // 添加元素
  // 给表单赋值
  textarea.value = text;
  // 判断光标是否在第一位置
  textarea.focus();
  if (textarea.setSelectionRange)
    textarea.setSelectionRange(0, textarea.value.length);
  // 获取光标起始位置到结束位置
  else textarea.select();
  try {
    flag = document.execCommand('copy'); // 执行复制
  } catch (eo) {
    flag = false;
  }
  document.body.removeChild(textarea); // 删除元素
  currentFocus?.focus();

  if (flag) {
    copySuccessTips(formatMessage);
  } else {
    copyErrorTips(formatMessage);
  }
}

export async function copyHtmlToClipboard(
  html: HTMLDivElement | null | undefined,
  formatMessage: any,
) {
  console.log(html)
  if (!html) {
    return notification.error({
      description: '无数据可以复制',
    });
  }
  const text = htmlToText(html);
  if (!navigator.clipboard) {
    return copyTextToClipBoard(text, formatMessage);
  }
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html.innerHTML], { type: 'text/html' }),
        'text/plain': new Blob([text], {
          type: 'text/plain',
        }),
      }),
    ]);
    copySuccessTips(formatMessage);
  } catch (error) {
    copyTextToClipBoard(html.innerText, formatMessage);
  }
}

export function replaceGptText(text: string) {
  const replaceTextList = DBO_FWC.get('ai_replace_text');
  const replacedSummary = replaceTextList.reduce(
    (per: string, cur: RegExp | string) => {
      return per.replace(cur, '');
    },
    text,
  );
  return replacedSummary;
}

/**
 * HTML预处理函数 - 修复嵌套列表结构和处理 font 标签
 * 主要用于wangEditor的内容预处理，解决HTML兼容性问题
 * @param html - 需要预处理的HTML字符串
 * @returns 预处理后的HTML字符串
 */
export function preprocessHtml(html: string): string {
  if (!html) return html;

  let processedHtml = html;

  // 处理 font 标签转换为 span 标签（wangEditor 不支持 font 标签）
  processedHtml = processedHtml.replace(
    /<font([^>]*)>(.*?)<\/font>/gi,
    (match, fontAttrs, content) => {
      // 解析 font 标签的属性
      const attrs: { [key: string]: string } = {};

      // 解析 color 属性
      const colorMatch = fontAttrs.match(/color\s*=\s*["']([^"']+)["']/i);
      if (colorMatch) {
        attrs.color = colorMatch[1];
      }

      // 解析 size 属性（转换为对应的 font-size）
      const sizeMatch = fontAttrs.match(/size\s*=\s*["']?(\d+)["']?/i);
      if (sizeMatch) {
        const size = parseInt(sizeMatch[1]);
        // font size 1-7 映射到像素大小
        const sizeMap: { [key: number]: string } = {
          1: '10px',
          2: '13px',
          3: '16px',
          4: '18px',
          5: '24px',
          6: '32px',
          7: '48px'
        };
        attrs.fontSize = sizeMap[size] || '16px';
      }

      // 解析 face 属性（字体名称）
      const faceMatch = fontAttrs.match(/face\s*=\s*["']([^"']+)["']/i);
      if (faceMatch) {
        attrs.fontFamily = faceMatch[1];
      }

      // 构建 style 属性
      const styles: string[] = [];
      if (attrs.color) styles.push(`color: ${attrs.color}`);
      if (attrs.fontSize) styles.push(`font-size: ${attrs.fontSize}`);
      if (attrs.fontFamily) styles.push(`font-family: ${attrs.fontFamily}`);

      const styleAttr = styles.length > 0 ? ` style="${styles.join('; ')}"` : '';

      return `<span${styleAttr}>${content}</span>`;
    }
  );

  // 改进的嵌套列表处理逻辑
  const processNestedLists = (html: string): string => {
    // 处理最常见的嵌套模式：<li>内容<ul>子列表</ul></li>
    // 将其转换为：<li>内容</li><ul>子列表</ul>

    // 第一步：处理基本嵌套模式
    let result = html.replace(
      /<li([^>]*)>\s*([^<]*?)(?:<[^>]*>[^<]*<\/[^>]*>)*\s*<ul([^>]*)>\s*(.*?)\s*<\/ul>\s*<\/li>/gis,
      (match, liAttrs, liText, ulAttrs, ulContent) => {
        // 确保 liText 不包含任何未闭合的标签
        if (liText.includes('<') && liText.includes('>')) {
          // 如果 liText 包含标签，可能是复杂结构，保持原样
          return match;
        }

        // 清理文本内容
        const cleanLiText = liText.trim();
        if (!cleanLiText) {
          return match;
        }

        return `<li${liAttrs}>${cleanLiText}</li><ul${ulAttrs}>${ulContent}</ul>`;
      }
    );

    // 第二步：处理包含简单标签的嵌套模式（如 span 标签）
    result = result.replace(
      /<li([^>]*)>\s*<span([^>]*)>(.*?)<\/span>\s*<ul([^>]*)>\s*(.*?)\s*<\/ul>\s*<\/li>/gis,
      (match, liAttrs, spanAttrs, spanContent, ulAttrs, ulContent) => {
        return `<li${liAttrs}><span${spanAttrs}>${spanContent}</span></li><ul${ulAttrs}>${ulContent}</ul>`;
      }
    );

    // 第三步：处理更复杂的嵌套模式（如包含 <p> 标签的）
    result = result.replace(
      /<li([^>]*)>\s*<p>\s*(.*?)\s*<\/p>\s*<ul([^>]*)>\s*(.*?)\s*<\/ul>\s*<\/li>/gis,
      (match, liAttrs, pContent, ulAttrs, ulContent) => {
        return `<li${liAttrs}><p>${pContent}</p></li><ul${ulAttrs}>${ulContent}</ul>`;
      }
    );

    return result;
  };

  // 应用改进的嵌套列表处理
  processedHtml = processNestedLists(processedHtml);

  // 处理 <li><p>内容</p></li> 的情况，去除无意义的 p 标签（在嵌套列表处理之后）
  // 只处理没有属性的 p 标签，保留有属性的 p 标签
  processedHtml = processedHtml.replace(
    /<li([^>]*)>\s*<p>\s*(.*?)\s*<\/p>\s*<\/li>(?!<ul>)/gis,
    (match, liAttrs, pContent) => {
      return `<li${liAttrs}>${pContent}</li>`;
    }
  );

  return processedHtml;
}
